import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Search, Package, FileText, FlaskConical, Flame, 
  Calendar, ChevronRight, AlertCircle, Loader2, Download, BoxIcon, CheckCircle, XCircle, ChefHat
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { cs } from 'date-fns/locale';
import { ExportProgress } from '../components/ExportProgress';

const BatchSearchPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Export progress state
  const [exportJobId, setExportJobId] = useState(null);
  const [showExportProgress, setShowExportProgress] = useState(false);

  // Load initial search from URL
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setSearchTerm(q);
      performSearch(q);
    }
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await api.get(`/batch/suggestions?q=${encodeURIComponent(query)}`);
      setSuggestions(res.data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  }, []);

  const performSearch = async (query) => {
    if (!query || query.length < 2) {
      toast.error('Zadejte alespoň 2 znaky');
      return;
    }

    setLoading(true);
    setSearched(true);
    setShowSuggestions(false);
    setSearchParams({ q: query });

    try {
      const res = await api.get(`/batch/search?batch=${encodeURIComponent(query)}`);
      setResults(res.data);
      
      // Show warning if there were partial errors (e.g. timeout on some collections)
      if (res.data.errors && res.data.errors.length > 0) {
        const errorModules = res.data.errors.map(e => e.split(':')[0]).join(', ');
        toast.warning(`Některé moduly se nepodařilo prohledat: ${errorModules}. Zkuste to znovu.`, {
          duration: 6000
        });
      }
    } catch (error) {
      toast.error('Chyba při vyhledávání');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch(searchTerm);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= 2) {
      fetchSuggestions(value);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setSearchTerm(suggestion);
    setShowSuggestions(false);
    performSearch(suggestion);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      // Try ISO format first
      if (dateStr.includes('T')) {
        return format(parseISO(dateStr), 'd. M. yyyy', { locale: cs });
      }
      // Try yyyy-MM-dd format
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return `${parts[2]}. ${parts[1]}. ${parts[0]}`;
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  const totalResults = results 
    ? results.counts.reports + results.counts.sensory + results.counts.autoclave + (results.counts.balirna || 0) + (results.counts.kitchen || 0)
    : 0;

  const handleExportZip = async () => {
    if (!results || totalResults === 0) {
      toast.error('Nejsou žádné záznamy k exportu');
      return;
    }

    setExporting(true);
    try {
      // Start background export
      const response = await api.post(`/batch/export-zip/start?batch=${encodeURIComponent(results.batch)}`);
      setExportJobId(response.data.job_id);
      setShowExportProgress(true);
      toast.success('Export spuštěn');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Chyba při spuštění exportu');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!results || totalResults === 0) {
      toast.error('Nejsou žádné záznamy k exportu');
      return;
    }

    setExporting(true);
    try {
      toast.info('Generuji PDF...');
      const response = await api.get(`/batch/export-pdf?batch=${encodeURIComponent(results.batch)}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sarze_${results.batch.replace(/[\/\s]/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF vygenerováno');
    } catch (error) {
      console.error('Export PDF error:', error);
      if (error.response?.status === 404) {
        toast.error('Žádné záznamy k exportu');
      } else {
        toast.error('Chyba při generování PDF');
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 py-4">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Detail šarže</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Vyhledejte záznamy podle šarže napříč všemi moduly</p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <form onSubmit={handleSearch} className="relative">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  value={searchTerm}
                  onChange={handleInputChange}
                  onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="Zadejte šarži pro vyhledání..."
                  className="pl-10 h-12 text-lg"
                  autoFocus
                />
                
                {/* Suggestions dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden">
                    {suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleSelectSuggestion(suggestion)}
                        className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100 last:border-0"
                      >
                        <Package className="h-4 w-4 text-slate-400" />
                        <span className="font-medium">{suggestion}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button type="submit" className="h-12 px-6 bg-teal-600 hover:bg-teal-700" disabled={loading}>
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                <span className="ml-2 hidden sm:inline">Hledat</span>
              </Button>
            </div>
          </form>
        </div>

        {/* Results */}
        {loading && (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-teal-600" />
            <p className="mt-2 text-slate-500">Vyhledávání...</p>
          </div>
        )}

        {!loading && searched && results && (
          <div className="space-y-6">
            {/* Error warning if some modules failed */}
            {results.errors && results.errors.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Některé moduly se nepodařilo prohledat</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Chyby v modulech: {results.errors.map(e => e.split(':')[0]).join(', ')}. 
                    Výsledky mohou být neúplné. Zkuste vyhledávání opakovat.
                  </p>
                </div>
              </div>
            )}
            
            {/* Summary */}
            <div className="flex flex-wrap gap-4">
              <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Package className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totalResults}</p>
                  <p className="text-sm text-slate-500">Celkem záznamů</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-blue-200 px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{results.counts.reports}</p>
                  <p className="text-sm text-blue-600">Hlášení</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-purple-200 px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <FlaskConical className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700">{results.counts.sensory}</p>
                  <p className="text-sm text-purple-600">Senzorika</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-teal-200 px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                  <Flame className="h-5 w-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-teal-700">{results.counts.autoclave}</p>
                  <p className="text-sm text-teal-600">Autokláv</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-orange-200 px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <BoxIcon className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-700">{results.counts.balirna || 0}</p>
                  <p className="text-sm text-orange-600">Balírna</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-amber-200 px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <ChefHat className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{results.counts.kitchen || 0}</p>
                  <p className="text-sm text-amber-600">Kuchyně</p>
                </div>
              </div>
              
              {/* Export buttons */}
              {totalResults > 0 && (
                <div className="flex gap-2">
                  <Button
                    onClick={handleExportPdf}
                    disabled={exporting}
                    className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white px-4 py-3 h-auto"
                  >
                    {exporting ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <FileText className="h-5 w-5 mr-2" />
                    )}
                    <div className="text-left">
                      <p className="font-semibold">Export PDF</p>
                      <p className="text-xs opacity-80">Souhrn záznamů</p>
                    </div>
                  </Button>
                  <Button
                    onClick={handleExportZip}
                    disabled={exporting}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-4 py-3 h-auto"
                  >
                    {exporting ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <Download className="h-5 w-5 mr-2" />
                    )}
                    <div className="text-left">
                      <p className="font-semibold">Export ZIP</p>
                      <p className="text-xs opacity-80">{totalResults} souborů</p>
                    </div>
                  </Button>
                </div>
              )}
            </div>

            {/* No results */}
            {totalResults === 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <AlertCircle className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Žádné záznamy nenalezeny</h3>
                <p className="text-slate-500">Pro šarži &quot;{results.batch}&quot; nebyly nalezeny žádné záznamy</p>
              </div>
            )}

            {/* Reports */}
            {results.reports.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">Hlášení ({results.reports.length})</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {results.reports.map((report) => (
                    <div
                      key={report.id}
                      onClick={() => navigate(`/reports/${report.id}`)}
                      className="p-4 hover:bg-slate-50 cursor-pointer flex items-center justify-between gap-4 group"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 group-hover:text-blue-700 transition-colors truncate">
                          {report.title || 'Bez názvu'}
                        </p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Package className="h-3.5 w-3.5" />
                            {report.batch}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDate(report.created_at)}
                          </span>
                          {report.category && (
                            <span>{report.category}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="bg-slate-100">
                          {report.status || 'Nový'}
                        </Badge>
                        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-blue-600" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sensory Tests */}
            {results.sensory_tests.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="bg-purple-50 px-4 py-3 border-b border-purple-100 flex items-center gap-2">
                  <FlaskConical className="h-5 w-5 text-purple-600" />
                  <h3 className="font-semibold text-purple-900">Senzorické zkoušky ({results.sensory_tests.length})</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {results.sensory_tests.map((test) => (
                    <div
                      key={test.id}
                      onClick={() => navigate(`/senzorika/${test.sensory_type || 'hotovky'}/${test.id}`)}
                      className="p-4 hover:bg-slate-50 cursor-pointer flex items-center justify-between gap-4 group"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 group-hover:text-purple-700 transition-colors truncate">
                          {test.food_name}
                          {test.sensory_type && (
                            <span className="ml-2 text-xs font-normal text-slate-500">
                              ({test.sensory_type === 'hotovky' ? 'Hotovky' : 'Krabičky'})
                            </span>
                          )}
                        </p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Package className="h-3.5 w-3.5" />
                            {test.batch}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDate(test.test_date)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={
                          test.evaluation_count > 0 ? 'bg-green-100 text-green-700' : 'bg-slate-100'
                        }>
                          {test.evaluation_count} hodnocení
                        </Badge>
                        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-purple-600" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Autoclave Records */}
            {results.autoclave_records.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="bg-teal-50 px-4 py-3 border-b border-teal-100 flex items-center gap-2">
                  <Flame className="h-5 w-5 text-teal-600" />
                  <h3 className="font-semibold text-teal-900">Záznamy autoklávu ({results.autoclave_records.length})</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {results.autoclave_records.map((record) => (
                    <div
                      key={record.id}
                      onClick={() => navigate(`/autoklav/${record.id}`)}
                      className="p-4 hover:bg-slate-50 cursor-pointer flex items-center justify-between gap-4 group"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 group-hover:text-teal-700 transition-colors">
                          {record.device_number} - {record.device_name}
                        </p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDate(record.date)}
                          </span>
                          <span>{record.program_name}</span>
                        </div>
                        {/* Matching food items */}
                        {record.matching_food_items?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {record.matching_food_items.map((item, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs">
                                <Package className="h-3 w-3" />
                                {item.food_name} ({item.quantity} ks) - {item.batch}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={
                          record.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }>
                          {record.status === 'completed' ? 'Dokončeno' : 'Probíhá'}
                        </Badge>
                        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-teal-600" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Balírna Records */}
            {results.balirna_records?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="bg-orange-50 px-4 py-3 border-b border-orange-100 flex items-center gap-2">
                  <BoxIcon className="h-5 w-5 text-orange-600" />
                  <h3 className="font-semibold text-orange-900">Balírna ({results.balirna_records.length})</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {results.balirna_records.map((record) => (
                    <div
                      key={record.id}
                      className="p-4 hover:bg-orange-50 cursor-pointer transition-colors group flex justify-between items-center"
                      onClick={() => navigate(`/balirna/${record.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-slate-900">{record.product_name || 'Bez názvu'}</p>
                          <span className="text-xs text-slate-400 font-mono">{record.batch_number}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {record.date ? format(parseISO(record.date), 'd. MMMM yyyy', { locale: cs }) : '-'}
                          </span>
                          <span>Zapsal: {record.created_by_name || '-'}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          {record.packaging_correct !== undefined && (
                            <span className={`inline-flex items-center gap-1 text-xs ${record.packaging_correct ? 'text-green-600' : 'text-red-600'}`}>
                              {record.packaging_correct ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                              Obal {record.packaging_correct ? 'OK' : 'NOK'}
                            </span>
                          )}
                          {record.passes_metal_detector !== undefined && (
                            <span className={`inline-flex items-center gap-1 text-xs ${record.passes_metal_detector ? 'text-green-600' : 'text-red-600'}`}>
                              {record.passes_metal_detector ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                              Metal detektor
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={
                          record.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }>
                          {record.status === 'completed' ? 'Dokončeno' : 'Rozpracováno'}
                        </Badge>
                        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-orange-600" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Kitchen Handovers */}
            {results.kitchen_handovers?.length > 0 && (
              <div className="bg-amber-50 rounded-xl border border-amber-200 overflow-hidden">
                <div className="px-4 py-3 bg-amber-100 border-b border-amber-200 flex items-center gap-2">
                  <ChefHat className="h-5 w-5 text-amber-700" />
                  <h3 className="font-semibold text-amber-900">Předávky z kuchyně ({results.kitchen_handovers.length})</h3>
                </div>
                <div className="divide-y divide-amber-200">
                  {results.kitchen_handovers.map((handover) => (
                    <div
                      key={handover.id}
                      className="p-4 hover:bg-amber-100 transition-colors cursor-pointer group flex items-center justify-between"
                      onClick={() => navigate(`/kuchyne/${handover.production_record_id}`)}
                    >
                      <div>
                        <p className="font-medium text-amber-900 group-hover:text-amber-700">
                          {handover.product_name || 'Předávka'}
                        </p>
                        <div className="text-sm text-amber-600 flex items-center gap-3 mt-1">
                          <span className="flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            Šarže: {handover.batch}
                          </span>
                          {handover.confirmed_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(parseISO(handover.confirmed_at), 'd. M. yyyy HH:mm', { locale: cs })}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-amber-500 mt-1">
                          <span>Potvrdil: {handover.confirmed_by_name || '-'}</span>
                          {handover.items?.length > 0 && (
                            <span className="ml-3">
                              Položky: {handover.items.map(i => `${i.name} (${i.net_weight || i.quantity} ${i.unit})`).join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="bg-green-100 text-green-700">
                          Potvrzeno
                        </Badge>
                        <ChevronRight className="h-5 w-5 text-amber-400 group-hover:text-amber-600" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Initial state */}
        {!loading && !searched && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-teal-100 flex items-center justify-center">
              <Package className="h-8 w-8 text-teal-600" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">Vyhledejte šarži</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              Zadejte číslo šarže a vyhledejte všechny související záznamy v modulech Hlášení, Senzorika, Autokláv, Balírna a Kuchyně
            </p>
          </div>
        )}
      </div>
      
      {/* Export Progress Dialog */}
      <ExportProgress
        jobId={exportJobId}
        isOpen={showExportProgress}
        onClose={() => setShowExportProgress(false)}
        title={`Export šarže ${results?.batch || ''}`}
      />
    </Layout>
  );
};

export default BatchSearchPage;
